import type { RefObject } from "react"
import { useCallback, useState } from "react"

import { toPng, toSvg } from "html-to-image"
import jsPDF from "jspdf"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import { logger } from "@/lib/logger"

type UseDashboardExportOptions = {
  containerRef: RefObject<HTMLDivElement | null>
  title: string
}

function sanitizeFilename(title: string): string {
  return title.replace(/[^a-z0-9]/gi, "_") || "dashboard"
}

function downloadDataUrl(dataUrl: string, filename: string) {
  const link = document.createElement("a")
  link.href = dataUrl
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

export function useDashboardExport({ containerRef, title }: UseDashboardExportOptions) {
  const { t } = useTranslation()
  const [isExporting, setIsExporting] = useState(false)

  const exportAsPng = useCallback(async () => {
    const node = containerRef.current
    if (!node) return
    setIsExporting(true)
    try {
      const dataUrl = await toPng(node, {
        backgroundColor: "#ffffff",
        pixelRatio: 2,
        height: node.scrollHeight,
        width: node.scrollWidth,
      })
      downloadDataUrl(dataUrl, `${sanitizeFilename(title)}.png`)
      toast.success(t("dash.dashboard.exported_png"))
    } catch (err: unknown) {
      logger.error("Dashboard export failed", err instanceof Error ? err : undefined)
      toast.error(t("dash.dashboard.export_error"))
    } finally {
      setIsExporting(false)
    }
  }, [containerRef, title, t])

  const exportAsSvg = useCallback(async () => {
    const node = containerRef.current
    if (!node) return
    setIsExporting(true)
    try {
      const dataUrl = await toSvg(node, {
        backgroundColor: "#ffffff",
        height: node.scrollHeight,
        width: node.scrollWidth,
      })
      downloadDataUrl(dataUrl, `${sanitizeFilename(title)}.svg`)
      toast.success(t("dash.dashboard.exported_svg"))
    } catch (err: unknown) {
      logger.error("Dashboard export failed", err instanceof Error ? err : undefined)
      toast.error(t("dash.dashboard.export_error"))
    } finally {
      setIsExporting(false)
    }
  }, [containerRef, title, t])

  const exportAsPdf = useCallback(async () => {
    const node = containerRef.current
    if (!node) return
    setIsExporting(true)
    try {
      const nodeWidth = node.offsetWidth
      const nodeHeight = node.offsetHeight
      const isLandscape = nodeWidth > nodeHeight

      const dataUrl = await toPng(node, {
        backgroundColor: "#ffffff",
        pixelRatio: 2,
        height: node.scrollHeight,
        width: node.scrollWidth,
      })

      const orientation = isLandscape ? "landscape" : "portrait"
      const doc = new jsPDF({ orientation, unit: "mm", format: "a4" })
      const pageWidth = doc.internal.pageSize.getWidth()
      const pageHeight = doc.internal.pageSize.getHeight()

      const margin = 10
      const titleHeight = 12

      doc.setFontSize(14)
      doc.text(title || t("dash.dashboard.untitled"), margin, margin + 6)

      const availableWidth = pageWidth - margin * 2
      const availableHeight = pageHeight - margin * 2 - titleHeight
      const ratio = nodeWidth / nodeHeight
      const scaledWidth = Math.min(availableWidth, availableHeight * ratio)
      const scaledHeight = scaledWidth / ratio

      doc.addImage(dataUrl, "PNG", margin, margin + titleHeight, scaledWidth, scaledHeight)
      doc.save(`${sanitizeFilename(title)}.pdf`)
      toast.success(t("dash.dashboard.exported_pdf"))
    } catch (err: unknown) {
      logger.error("Dashboard export failed", err instanceof Error ? err : undefined)
      toast.error(t("dash.dashboard.export_error"))
    } finally {
      setIsExporting(false)
    }
  }, [containerRef, title, t])

  return { exportAsPng, exportAsSvg, exportAsPdf, isExporting }
}
